import React, { type PropsWithChildren } from 'react';

import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';

import rightSectionBgDark from '../../../assets/right_section_bg_dark.svg';
import rightSectionBgLight from '../../../assets/right_section_bg_light.svg';
import { messages } from './messages';

export interface LayoutProps {
	children: React.ReactNode;
	vector: {
		light?: string;
		dark?: string;
		component?: React.ReactNode;
	};
	currentStep: number;
	onClose: () => void;
	actionButtons: { primary: React.ReactNode; secondary?: React.ReactNode };
}

export const Layout = ({
	children,
	vector,
	actionButtons,
	currentStep,
	onClose,
}: PropsWithChildren<LayoutProps>) => {
	const { formatMessage } = useIntl();
	const theme = useThemeObserver();
	const isDarkMode = theme.colorMode === 'dark';

	const shouldShowStep = currentStep !== 5;

	return (
		<Flex xcss={contentContainerStyles} justifyContent="space-between">
			<Flex direction="column" xcss={[halfWidthStyles]} justifyContent="space-between">
				<Flex direction="column" rowGap="space.100" xcss={[contentStyles]}>
					{children}
				</Flex>
				<Flex
					xcss={[actionStyles]}
					justifyContent={shouldShowStep ? 'space-between' : 'end'}
					alignItems="center"
				>
					{shouldShowStep && (
						<Box as="span" xcss={locatorStyles}>
							{formatMessage(messages.stepLocatorNonFinal, {
								currentStep,
							})}
						</Box>
					)}
					<Flex columnGap="space.100">
						{actionButtons.secondary}
						{actionButtons.primary}
					</Flex>
				</Flex>
			</Flex>
			<Flex
				xcss={[
					halfWidthStyles,
					rightColumnStyles,
					isDarkMode ? rightColumnDarkStyles : rightColumnLightStyles,
				]}
			>
				{vector.component ? (
					<Flex alignItems="center" justifyContent="center" xcss={vectorComponentProps}>
						{vector.component}
					</Flex>
				) : (
					<Box
						role="presentation"
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{
							backgroundImage: `url(${isDarkMode ? vector?.dark : vector?.light})`,
						}}
						xcss={rightVectorStyles}
					/>
				)}
			</Flex>
			<Box xcss={closeIconStyles}>
				<Button
					appearance="subtle"
					iconBefore={<CrossIcon label="" />}
					aria-label={formatMessage(messages.closeButtonNonFinal)}
					onClick={onClose}
				/>
			</Box>
		</Flex>
	);
};

const contentContainerStyles = xcss({
	height: '100%',
	position: 'relative',
});

const vectorComponentProps = xcss({
	flex: '1',
});

const halfWidthStyles = xcss({ width: '50%', flexShrink: 0 });
const contentStyles = xcss({ margin: 'space.600', padding: 'space.200' });

const actionStyles = xcss({
	padding: 'space.200',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	borderLeft: 'none',
	borderRight: 'none',
	borderBottom: 'none',
});

const locatorStyles = xcss({ color: 'color.text.subtlest' });

const rightColumnStyles = xcss({
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	borderTopRightRadius: 'border.radius.100',
	borderBottomRightRadius: 'border.radius.100',
	overflow: 'hidden',
});

const rightVectorStyles = xcss({
	width: '100%',
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'contain',
	backgroundPosition: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const rightColumnLightStyles = xcss({ backgroundImage: `url(${rightSectionBgLight})` });
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const rightColumnDarkStyles = xcss({ backgroundImage: `url(${rightSectionBgDark})` });
const closeIconStyles = xcss({
	position: 'absolute',
	right: 'space.200',
	top: 'space.200',
});
